import {AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {debounceTime, delay, distinctUntilChanged, skip, take, takeUntil, tap} from 'rxjs/operators';
import {TranslateService} from '@ngx-translate/core';
import {BehaviorSubject, fromEvent, merge, Observable, of, Subscription} from 'rxjs';
import {SelectionModel} from '@angular/cdk/collections';
import {select, Store} from '@ngrx/store';
import {MatDialog} from '@angular/material/dialog';
import {MatPaginator} from '@angular/material/paginator';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatSort} from '@angular/material/sort';
import {
    AssignmentModel,
    AssignmentsDataSource,
    AssignmentsPageRequested,
    AssignmentsStatusUpdated,
    AssignmentOnServerRestored,
    AssignmentActionToggleLoading,
    OneAssignmentDeleted,
    AssignmentDeletedFromTrash,
    AssignmentTrashFlushed, selectTrashedAssignmentCount, selectAdminTrashedAssignmentCount, AssignmentOnServerAdminRestored,
    AssignmentDeletedFromAdminTrash,
    selectAssignmentLastQuery,
    AssignmentsService
} from '../../../../../core/assignment';
import {ActivatedRoute, Router} from '@angular/router';
import {LayoutUtilsService, QueryParamsModel} from '../../../../../core/_base/crud';
import {SubheaderService} from '../../../../../core/_base/layout';
import {AppState} from '../../../../../core/reducers';
import {TrashAction} from '../../../../partials/content/crud/trash-dialog/trash-dialog.component';
import {AssignmentDuplicateDialogComponent} from '../_sub/assignment-duplicate-dialog/assignment-duplicate-dialog.component';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { Update } from '@ngrx/entity';
import { AssignmentStatusUpdated } from 'src/app/core/assignment/_actions/assignment.actions';
import { Option } from '../../../shared_components/filter/f-selection/f-selection.component';
import { AllAgenciesRequested, selectAllAgencies } from 'src/app/core/admin';
import { concat } from 'lodash';
import { AllUsersOfRoleRequested, selectValuersInStore } from 'src/app/core/auth';
import { User, currentUser, hasPermission } from 'src/app/core/mad-auth/mad-auth.store';
import { DuplicateAssignmentDialogComponent } from '../../components/duplicate-assignment-dialog/duplicate-assignment-dialog.component';
import { SubdomainService } from 'src/app/core/_base/subdomain.service';
type pmStatus = {
    total_assignments: string,
    total_toes: string,
    total_properties: string,
    total_valuations: string,
}

@Component({
    selector: 'kt-assignments-list',
    templateUrl: './assignments-list.component.html',
    styleUrls: ['./assignments-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})

export class AssignmentsListComponent implements OnInit, OnDestroy, AfterViewInit, TrashAction {

    // Table fields
    dataSource: AssignmentsDataSource;
    displayedColumns = [
        'id', 'name', 'manager.first_name', 'agency_name', 'toe_cnt',
        'ongoing_toe_cnt', 'actions'
    ];

    trashCnt$: Observable<number>;
    adminTrashCnt$: Observable<number>;

    @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;
    @ViewChild('sort1', {static: true}) sort: MatSort;
    // Selection
    selection = new SelectionModel<AssignmentModel>(true, []);
    assignmentResult: AssignmentModel[] = [];

    lastQueryParams: QueryParamsModel;
    assignmentStatusCnts$ = new BehaviorSubject<pmStatus>({
        total_assignments: '0',
        total_toes: '0',
        total_properties: '0',
        total_valuations: '0',
    });
    filter: any;
    projectManagerSelections: Option[] = [{id: -1, text: 'All'}]
    agencySelections: Option[] = [{id: -1, text: 'All'}];
    status: Option[] = [{id: -1, text: 'All'}, {id: 0, text: 'Draft'}, {id: 1, text: 'Ongoing'}]

    // Subscriptions
    private subscriptions: Subscription[] = [];
    canAccessAdminTrash$ = this.store.pipe(select(hasPermission(['admin_trash'])))

    currentUser: User|null = null;

    /**
     * Component constructor
     *
     * @param activatedRoute
     * @param router
     * @param dialog: MatDialog
     * @param snackBar: MatSnackBar
     * @param subheaderService
     * @param service
     * @param layoutUtilsService: LayoutUtilsService
     * @param translate: TranslateService
     * @param store: Store<AppState>
     */
    constructor(private activatedRoute: ActivatedRoute,
                private router: Router,
                public dialog: MatDialog,
                public snackBar: MatSnackBar,
                public subheaderService: SubheaderService,
                public service: AssignmentsService,
                private layoutUtilsService: LayoutUtilsService,
                private translate: TranslateService,
                private store: Store<AppState>,
                public subDomainService: SubdomainService) {
    }

    /**
     * @ Lifecycle sequences => https://angular.io/guide/lifecycle-hooks
     */

    /**
     * On init
     */
    ngOnInit() {
        this.lastQueryParams = new QueryParamsModel({});
        const userSub = this.store.pipe(select(currentUser)).subscribe(user => {
            this.currentUser = user;
        })
        this.subscriptions.push(userSub)

        this.subheaderService.setTitle('Assignments');
        this.subheaderService.setBreadcrumbs([
            {title: 'Main menu', page: `../default/assignment`},
            {title: 'Assignments', page: `../default/assignment/assignments`},
        ]);
        this.paginator._intl.itemsPerPageLabel = this.translate.instant('GENERAL.PAGELABEL');
        // If the user changes the sort order, reset back to the first page.
        const sortSubscription = this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
        this.subscriptions.push(sortSubscription);

        /* Data load will be triggered in two cases:
        - when a pagination event occurs => this.paginator.page
        - when a sort event occurs => this.sort.sortChange
        **/
        const paginatorSubscriptions = merge(this.sort.sortChange, this.paginator.page).pipe(
            tap(() => this.loadAssignmentsList())
        )
            .subscribe();
        this.subscriptions.push(paginatorSubscriptions);

        // Init DataSource
        this.dataSource = new AssignmentsDataSource(this.store);
        const entitiesSubscription = this.dataSource.entitySubject.pipe(
            skip(1),
            distinctUntilChanged()
        ).subscribe(res => {
            if (res.length > 0) {
                this.assignmentResult = res;
                this.assignmentStatusCnts$.next({
                    total_assignments: res.length.toString(),
                    total_toes: res.reduce((total, item) => total + item.toe_cnt, 0).toString(),
                    total_properties: res.reduce((total, item) => total + item.tp_cnt, 0).toString(),
                    total_valuations: res.reduce((total, item) => total + item.val_cnt, 0).toString(),
                })
            } else {
                this.assignmentResult = []
                this.assignmentStatusCnts$.next({
                    total_assignments: '0',
                    total_toes: '0',
                    total_properties: '0',
                    total_valuations: '0',
                })
            }
        });

        this.trashCnt$ = this.store.pipe(select(selectTrashedAssignmentCount));
        this.adminTrashCnt$ = this.store.pipe(select(selectAdminTrashedAssignmentCount));

        this.subscriptions.push(entitiesSubscription);
        // First load
        of(undefined).pipe(take(1), delay(1000)).subscribe(() => { // Remove this line, just loading imitation
            this.loadAssignmentsList();
        }); // Remove this line, just loading imitation

        const lastQuerySubscription = this.store.pipe(
            select(selectAssignmentLastQuery),
            take(1)
        ).subscribe(res => {
            if (res.isEmpty()) {
                return;
            }
            this.lastQueryParams = res;
            this.updateFilter(res.filter);
        });
        this.subscriptions.push(lastQuerySubscription);

        this.store.dispatch(new AllAgenciesRequested());
        const agencySub = this.store.pipe(select(selectAllAgencies)).subscribe(res => {
            this.agencySelections = concat([{id: -1, text: 'All'}], res ? res.map(item => ({id: item.id, text: item.name})) : []);
        });
        this.subscriptions.push(agencySub);
        this.store.dispatch(new AllUsersOfRoleRequested({roleId: 2}));
        const userSubscription = this.store.pipe(
            select(selectValuersInStore))
            .subscribe(res => {
                this.projectManagerSelections = concat([{id: -1, text: 'All'}], res ? res.items.map(item => ({id: item.id, text: item.full_name})) : [])
            });
        this.subscriptions.push(userSubscription);
    }

    /**
     * After init
     */
    ngAfterViewInit(): void {
        if (this.activatedRoute.snapshot.queryParamMap.has('show-trash')) {
            this.trash();
        }

        if (this.activatedRoute.snapshot.queryParamMap.has('show-admin-trash')) {
            this.adminTrash();
        }
    }

    /**
     * On Destroy
     */
    ngOnDestroy() {
        this.subscriptions.forEach(el => el.unsubscribe());
    }

    /**
     * Load Assignments List from service through data-source
     */
    loadAssignmentsList() {
        this.selection.clear();
        let queryParams = null;
        if (this.lastQueryParams.isEmpty()) {
            queryParams = new QueryParamsModel(
                this.filter,
                this.sort.direction,
                this.sort.active,
                this.paginator.pageIndex + 1,
                this.paginator.pageSize
            );
        } else {
            queryParams = this.lastQueryParams;
        }
        this.lastQueryParams = new QueryParamsModel({});
        // Call request from server
        this.store.dispatch(new AssignmentsPageRequested({page: queryParams}));
        this.selection.clear();
    }


    updateFilter(filter: any) {
        this.filter = filter;
        this.loadAssignmentsList();
    }

    convertManager(assignment: any) {
        return {
            firstName: assignment.manager.first_name,
            lastName: assignment.manager.last_name,
            email: assignment.manager.email,
            picture: assignment.manager.picture,
            qualification: assignment.manager.qualification_name,
            role: 'Project Manager',
            agency: assignment.agency_name
        }
    }

    /** ACTIONS */
    /**
     * Delete assignment
     *
     * @param _item: AssignmentModel
     */
    deleteAssignment(_item: AssignmentModel) {
        const _title = this.translate.instant('ASSIGNMENT.LIST.DIALOG.DELETE.TITLE');
        const _description = this.translate.instant('ASSIGNMENT.LIST.DIALOG.DELETE.DESCRIPTION', {name: _item.name});
        const _waitDesciption = this.translate.instant('ASSIGNMENT.LIST.DIALOG.DELETE.WAIT_DESCRIPTION');

        const dialogRef = this.layoutUtilsService.deleteElement(_title, _description, _waitDesciption);
        dialogRef.afterClosed().subscribe(res => {
            if (!res) {
                return;
            }
            this.store.dispatch(new OneAssignmentDeleted({id: _item.id}));
        });
    }

    /**
     * Show UpdateStatuDialog for selected assignments
     */
    updateStatusForAssignments() {
        const _title = 'updateStatusForAssignments';
        // const _updateMessage = this.translate.instant('ECOMMERCE.CUSTOMERS.UPDATE_STATUS.MESSAGE');
        const _statuses = [{value: 0, text: 'Suspended'}, {value: 1, text: 'Active'}, {value: 2, text: 'Pending'}];
        const _messages = [];

        this.selection.selected.forEach(elem => {
            _messages.push({
                text: `${elem.name}`,
                id: elem.id.toString(),
                status: elem.status,
                statusTitle: this.getItemStatusString(elem.status, elem.completion),
                statusCssClass: this.getItemCssClassByStatus(elem.status)
            });
        });

        const dialogRef = this.layoutUtilsService.updateStatusForEntities(_title, _statuses, _messages);
        dialogRef.afterClosed().subscribe(res => {
            if (!res) {
                this.selection.clear();
                return;
            }

            this.store.dispatch(new AssignmentsStatusUpdated({
                status: +res,
                assignments: this.selection.selected
            }));

            // this.layoutUtilsService.showActionNotification(_updateMessage, MessageType.Update, 10000, true, true);
            this.selection.clear();
        });
    }

    /**
     * Show add assignment dialog
     */
    addAssignment() {
        this.editAssignment(null);
    }

    /**
     * Show Edit assignment dialog and save after success close result
     * @param assignment: AssignmentModel
     */
    editAssignment(assignment: AssignmentModel) {
        if (assignment) {
            this.router.navigate(['../assignments/edit', assignment.id], {relativeTo: this.activatedRoute});
        } else {
            this.router.navigate(['../assignments/add'], {relativeTo: this.activatedRoute});
        }
    }

    dashboard(assignment: AssignmentModel) {
        this.router.navigate(['../assignments/dashboard/', assignment.id], {relativeTo: this.activatedRoute});
    }

    reachAssignment(assignment: AssignmentModel) {
        this.router.navigate(['../assignments/', assignment.id, 'toes'], {relativeTo: this.activatedRoute});
    }

    duplicateAssignment(assignment: AssignmentModel) {
        const dialogRef = this.dialog.open(DuplicateAssignmentDialogComponent, {
            data: {
                assignment: assignment
            }
        })
        dialogRef.afterClosed().subscribe(res => {
            if (!res) {
                return;
            }

            if (res == 'success') {
                this.loadAssignmentsList()    
            }
        })
    }

    trash() {
        this.store.dispatch(new AssignmentActionToggleLoading({isLoading: true}));
        this.service.getTrashed().subscribe(res => {
            const items = [];
            res.data.forEach(elem => {
                items.push({
                    text: `${elem.name}`,
                    id: elem.id.toString(),
                    date: elem.user_deleted,
                    deletedUser: elem.userDeletedBy,
                    hasPermanentlyDelete: true
                });
            });
            if (items.length > 0) {
                this.show_trash(items);
            }

        });
    }

    // Fetch admin trash data
    adminTrash() {
        this.store.dispatch(new AssignmentActionToggleLoading({isLoading: true}));
        this.service.getAdminTrashed().subscribe(res => {

            this.store.dispatch(new AssignmentActionToggleLoading({isLoading: false}));
            const items = [];
            res.data.forEach(elem => {
                items.push({
                    text: `${elem.name}`,
                    id: elem.id.toString(),
                    hasPermanentlyDelete: true,
                    date: elem.deleted_at,
                    deletedUser: elem.deletedBy,
                });
            });
            if (items.length > 0) {
                this.show_trash(items, true);
            }
        });
    }

    /**
     * Show Edit assignment dialog and save after success close result
     * @param items
     * @param isAdmin
     */
    show_trash(items: any[], isAdmin = false) {
        let _title = this.translate.instant('ASSIGNMENT.LIST.DIALOG.SHOW_TRASH.TITLE');
        if (isAdmin) {
            _title = this.translate.instant('ASSIGNMENT.LIST.DIALOG.SHOW_TRASH.TITLE_ADMIN');
        }
        this.layoutUtilsService.trashedEntities(_title, items, this, isAdmin);
    }

    hardDelete(id: number) {
        this.store.dispatch(new AssignmentDeletedFromAdminTrash({id}));
    }

    restore(id: number, isAdmin: boolean) {
        if (isAdmin) {
            this.store.dispatch(new AssignmentOnServerAdminRestored({id}));
        } else {
            this.store.dispatch(new AssignmentOnServerRestored({id}));
        }
    }

    delete(id: number) {
        this.store.dispatch(new AssignmentDeletedFromTrash({id}));
    }

    flush() {
        this.store.dispatch(new AssignmentTrashFlushed());
    }

    /**
     * Check all rows are selected
     */
    isAllSelected(): boolean {
        const numSelected = this.selection.selected.length;
        const numRows = this.assignmentResult.length;
        return numSelected === numRows;
    }

    /**
     * Toggle all selections
     */
    masterToggle() {
        if (this.selection.selected.length === this.assignmentResult.length) {
            this.selection.clear();
        } else {
            this.assignmentResult.forEach(row => this.selection.select(row));
        }
    }

    /** UI */
    /**
     * Retursn CSS Class Name by status
     *
     * @param percent: number
     */
    getItemCssClassByStatus(percent: number = 0): string {

        if (percent < 26) {
            return 'danger';
        }
        if (percent > 25 && percent < 51) {
            return 'warning';
        }
        if (percent > 50 && percent < 76) {
            return 'info';
        }
        // return 'primary';
        return 'success';
    }

    /**
     * Returns Item Status in string
     * @param status: number
     * @param completion: number
     */
    getItemStatusString(status: number = 0, completion: number): string {
        switch (status) {
            case 0:
            case 1:
                return this.translate.instant('ASSIGNMENT.LIST.DATATABLE.STATUS.VALUE.ONGOING', {completion});
            case 2:
                return 'Completed';
        }
        return '';
    }

    changeStatus(event: MatCheckboxChange, assignment: AssignmentModel) {
        const status = event.checked ? 2:1;
        const newAssignment: Update<AssignmentModel> = {
            id: assignment.id,
            changes: {
                status
            }
        }
        this.store.dispatch(new AssignmentStatusUpdated({updateAssignment: newAssignment}));
    }

    onFilterChange(event) {
        this.paginator.pageIndex = 0;
        this.filter = event;
        this.loadAssignmentsList();
    }
}
